import { EntityId } from '@reduxjs/toolkit';
import { ObjectId } from 'app/common.types';
import { ROUTER_API } from 'app/constants/api.constant';
import { LoyaltyCardInDTO, LoyaltyCardTransactionInDTO, RedeemOfferDTO } from 'app/main/apps/loyalties/type';
import { ReceiptModelInDTO } from 'app/main/apps/scanner/types';
import { StoreDTO } from 'app/main/apps/stores/type';
import { CouponStateEnum, CouponUserItemInDTO } from 'app/main/design-system/models/coupon.type';
import { ListObject } from 'app/main/design-system/models/listObjectsModel';
import BaseApi from 'app/services/api/base.api';
import {
  AcquiredFromTypeEnum,
  ActivityPeriod,
  AddClientDTO,
  AddClientInDTO,
  activityPeriodParams,
  ClientCreditPoints,
  ClientDetails,
  ClientEntityList,
  ClientFormResponse,
  ClientLoyalty,
  ClientRecord,
  ClientSearched,
  ClientSearchedType,
  ClientsFiltersUrlEnum,
  ClientsSortKey,
  ClientsSortOrderEnum,
  ClientTopProduct,
  ClientType,
  ColumnPreference,
  ColumnPreferences,
  CrmFilter,
  crmFilterParams,
  EditClientDTO,
  EtpReceiptDTO,
  FormQuestionColumn,
} from '../type';

export const clientsApiV2 = new BaseApi({ baseURL: ROUTER_API, tokenKey: 'token', timeout: 60000 });

export const getReceiptDetailApi = async (receiptId: EntityId) => {
  const response = await clientsApiV2.api.get<ReceiptModelInDTO>(`api/v2/receipts/${receiptId}`);

  return response.data;
};

export const getEtpReceiptDetailApi = async (etpReceiptId: EntityId) => {
  const response = await clientsApiV2.api.get<EtpReceiptDTO>(`api/v2/etp/receipts/${etpReceiptId}`);

  return response.data;
};

export const getUserClientsApi = async (
  sort?: ClientsSortKey,
  order?: ClientsSortOrderEnum,
  page?: number,
  size?: number,
  filter?: string,
  storeIds?: EntityId[],
  acquiredFrom?: AcquiredFromTypeEnum[],
  clientType?: ClientType[],
  segmentRefs?: string[],
  crmFilter?: CrmFilter,
  record?: ClientRecord,
  activityPeriod?: ActivityPeriod,
) => {
  const queryParams = new URLSearchParams();
  if (storeIds) {
    storeIds.forEach(storeId => {
      queryParams.append(ClientsFiltersUrlEnum.storeIds, storeId.toString());
    });
  }
  if (acquiredFrom) {
    acquiredFrom.forEach(format => {
      queryParams.append(ClientsFiltersUrlEnum.acquiredFrom, format);
    });
  }
  if (clientType) {
    clientType.forEach(format => {
      queryParams.append(ClientsFiltersUrlEnum.clientType, format);
    });
  }
  if (segmentRefs) {
    segmentRefs.forEach(ref => {
      queryParams.append(ClientsFiltersUrlEnum.segmentRefs, ref);
    });
  }

  if (crmFilter) {
    crmFilterParams(crmFilter).forEach(([key, value]) => queryParams.append(key, value));
  }
  if (record) {
    queryParams.append(ClientsFiltersUrlEnum.record, record);
  }
  if (activityPeriod) {
    activityPeriodParams(activityPeriod).forEach(([key, value]) => queryParams.append(key, value));
  }

  const response = await clientsApiV2.api.get<ClientEntityList>(`api/v2/franchises/clients?${queryParams.toString()}`, {
    params: { sort, order, page, size, filter },
  });

  return response.data || [];
};

const CLIENTS_SCREEN = 'clients';

export const getColumnPreferencesApi = async () => {
  const response = await clientsApiV2.api.get<ColumnPreferences>(
    `api/v2/users/me/column-preferences/${CLIENTS_SCREEN}`,
  );
  return response.data;
};

export const saveColumnPreferencesApi = async (columns: ColumnPreference[]) => {
  const response = await clientsApiV2.api.put<ColumnPreferences>(
    `api/v2/users/me/column-preferences/${CLIENTS_SCREEN}`,
    { columns },
  );
  return response.data;
};

export const getUserClientDetailApi = async (
  franchiseId: EntityId,
  clientId: ClientDetails['id'],
  storeId?: EntityId,
) => {
  const response = await clientsApiV2.api.get<ClientDetails>(`api/v2/franchises/${franchiseId}/clients/${clientId}`, {
    params: { storeId },
  });

  return response.data || {};
};

export const getClientTopProductsApi = async (
  franchiseId: EntityId,
  clientId: string,
  storeId?: string,
  limit?: number,
) => {
  const response = await clientsApiV2.api.get<ClientTopProduct[]>(
    `api/v2/franchises/${franchiseId}/clients/${clientId}/top-products`,
    {
      params: { storeId, limit },
    },
  );
  return response.data || [];
};

export const getClientLoyaltyApi = async (franchiseId: EntityId, clientId: string, storeId?: string) => {
  const response = await clientsApiV2.api.get<ClientLoyalty>(
    `api/v2/franchises/${franchiseId}/clients/${clientId}/loyalty`,
    {
      params: { storeId },
    },
  );
  return response.data;
};

export const updateCreditPointsClientApi = async (creditPointEntity: ClientCreditPoints) => {
  try {
    const response = await clientsApiV2.api.patch<LoyaltyCardInDTO>(`api/v2/loyalties/points`, creditPointEntity);
    return response.data || {};
  } catch (_error) {
    throw new Error('Erreur lors de la requête PATCH');
  }
};

export const addSaleToClientApi = async (storeId: string, userId: string, amount: number) => {
  const response = await clientsApiV2.api.post<LoyaltyCardInDTO>(`api/v2/stores/${storeId}/clients/${userId}/sale`, {
    amount,
  });
  return response.data;
};

export const searchClientApi = async (type: ClientSearchedType, value: string) => {
  const response = await clientsApiV2.api.get<ClientSearched>(`api/v2/users/search`, {
    params: {
      [type]: value,
    },
  });
  return response.data;
};

export const addClientApi = async (client: AddClientDTO, id: string) => {
  const response = await clientsApiV2.api.post<AddClientInDTO>(`api/v2/franchises/${id}/users`, client);
  return response.data;
};

export const updateClientApi = async (client: AddClientDTO, id: string) => {
  const response = await clientsApiV2.api.put<LoyaltyCardInDTO>(`api/v2/franchises/${id}/users`, client);
  return response.data;
};

export const updateClientInfoApi = async (client: EditClientDTO, id: string) => {
  const response = await clientsApiV2.api.patch<EditClientDTO>(`api/v2/users/${id}/info`, client);
  return response.data;
};

export const addClientToStoreApi = async (storeId: StoreDTO['id'], userId: string) => {
  const response = await clientsApiV2.api.post(`api/v2/stores/${storeId}/users/${userId}`, {});
  return response.data;
};

export const getUserCouponsApi = async (
  userId: EntityId,
  page?: number,
  size?: number,
  franchiseId?: EntityId,
  state?: CouponStateEnum,
  storeId?: EntityId,
) => {
  const response = await clientsApiV2.api.get<ListObject<CouponUserItemInDTO>>(`api/v2/users/${userId}/coupons`, {
    params: { page, size, franchiseId, storeId, state },
  });
  return response.data;
};

export const getUserClientTransactionsApi = async (
  userId: ObjectId,
  franchiseId: ObjectId,
  storeId: ObjectId,
  page?: number,
  size?: number,
) => {
  const response = await clientsApiV2.api.get<ListObject<LoyaltyCardTransactionInDTO>>(
    `api/v2/users/${userId}/transactions`,
    {
      params: { page, size, storeId, franchiseId },
    },
  );
  return response.data;
};

export const redeemOfferApi = async (userId: EntityId, storeId: EntityId, offerId: EntityId) => {
  const url = `api/v2/loyalties/offers/${offerId}/redeem`;
  const response = await clientsApiV2.api.post<RedeemOfferDTO>(url, { userId, storeId });
  return response.data;
};

export const redeemCouponApi = async (couponUserId: EntityId) => {
  const url = `api/v2/coupons-user/${couponUserId}/redeem`;
  const response = await clientsApiV2.api.post(url);
  return response.data;
};

export const deleteClientApi = async (franchiseId: EntityId, userId: EntityId) => {
  const response = await clientsApiV2.api.delete(`api/v2/franchises/${franchiseId}/clients/${userId}`);
  return response.data;
};

export const getFormQuestionColumnsApi = async () => {
  const response = await clientsApiV2.api.get<FormQuestionColumn[]>('api/v2/franchises/forms/question-columns');
  return response.data || [];
};

export const getClientFormResponsesApi = async (
  franchiseId: EntityId,
  clientId: ClientDetails['id'],
  page?: number,
  size?: number,
) => {
  const response = await clientsApiV2.api.get<ListObject<ClientFormResponse>>(
    `api/v2/franchises/${franchiseId}/clients/${clientId}/form-responses`,
    { params: { page, size } },
  );
  return response.data;
};
